import ImageIcon from '@mui/icons-material/Image'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import { IconButton, Tooltip } from '@mui/material'
import cn from 'classnames'
import ColorThief from 'colorthief'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from '../../config.json'

import ErrorNote from '../common/util/ErrorNote'
import Link from '../common/util/input/Link'
import { get } from '../helpers/Api'
import { rgbToHex } from '../helpers/FormatLinks'
import { Context, useSharedState } from '../waxplorer/Waxplorer'
import qs from 'qs'

export const UserImage = ({ user }) => {
    const IMG_CLASS = 'm-auto max-h-64 md:max-h-[70vh] md:py-[2.5vh] max-w-full'

    const [bgColor, setBgColor] = useState('#000000')
    const [ipfsHash, setIpfsHash] = useState(null)
    const [error, setError] = useState(null)

    const [userPicture, setUserPicture] = useState(null)

    const parseUserPicture = (res) => {
        if (res && res.image) {
            setUserPicture(res.image)
        }
    }

    const [state] = useSharedState()

    useEffect(() => {
        if (userName === user && state.userPictureChanged) {
            setTimeout(() => {
                get('user-picture/' + user, 'legacy').then(parseUserPicture)
            }, 3000)
        } else if (user) {
            get('user-picture/' + user, 'legacy').then(parseUserPicture)
        }
    }, [user, state.userPictureChanged])

    const { t } = useTranslation('common')

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const defaultImg = 'QmVVE5aLtq4EtMe4Khev5CSic3AKqX2RuNTAB79thvpqQW'
    const image =
        ipfsHash && ipfsHash !== defaultImg
            ? ipfsHash
            : userPicture
            ? userPicture
            : defaultImg

    const updatePicture = (pictureInfo) => {
        if (pictureInfo) {
            if (pictureInfo['error']) {
                setError(pictureInfo['error'])
            } else if (pictureInfo['new']) {
                setIpfsHash(pictureInfo['new'])
                dispatch({ type: 'SET_USER_PICTURE_CHANGED', payload: true })
            }
        }
    }

    const changePicture = () => {
        dispatch({ type: 'SET_CALLBACK', payload: updatePicture })
        dispatch({ type: 'SET_ACTION', payload: 'addpicture' })
        dispatch({ type: 'SET_ASSET', payload: image })
    }

    const imageURL = image.includes('http') ? image : config.ipfs + image

    useEffect(() => {
        if (userName !== user) {
            get(`get-friends?limit=1&user=${userName}&term=${user}`).then(
                parseIsFriend,
            )
        }
    }, [user, userName])

    const parseIsFriend = (res) => {
        setIsFriend(res && res.length > 0 && res[0]['user'] === user)
    }

    useEffect(() => {
        if (imageURL) {
            const colorThief = new ColorThief()
            const img = new Image()
            img.addEventListener('load', () => {
                try {
                    const color = colorThief.getColor(img, 30)
                    const rgb = rgbToHex(color[0], color[1], color[2])
                    setBgColor(rgb)
                } catch (e) {
                    console.error(e)
                }
            })
            let googleProxyURL =
                'https://images1-focus-opensocial.googleusercontent.com/gadgets/proxy?container=focus&refresh=2592000&url='
            img.crossOrigin = 'Anonymous'
            img.src = googleProxyURL + encodeURIComponent(imageURL)
        }
    }, [imageURL])

    const [isFriend, setIsFriend] = useState(false)

    const removeFriend = async () => {
        try {
            await activeUser.session.transact(
                {
                    actions: [
                        {
                            account: 'atomhubtools',
                            name: 'remaccvalues',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                account: userName,
                                list_name: 'friends',
                                values_to_remove: [user],
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            setIsFriend(false)
        } catch (e) {
            setError(e.message)
        }
    }

    const addFriend = async () => {
        try {
            await activeUser.session.transact(
                {
                    actions: [
                        {
                            account: 'atomhubtools',
                            name: 'addaccvalues',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                account: userName,
                                list_name: 'friends',
                                values_to_add: [user],
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            setIsFriend(true)
        } catch (e) {
            setError(e.message)
        }
    }

    const [, dispatch] = useContext(Context)

    const toggleImage = (img) => {
        dispatch({ type: 'FULL_IMAGE', payload: img })
    }

    return (
        <div
            className={cn(
                'w-full h-full border bg-white dark:!bg-paper border-zinc-200 dark:!border-paperl shadow-xl rounded-md',
            )}
        >
            <div className="p-2 h-16 w-full flex border-b border-zinc-200 dark:!border-paperl">
                <div className="text-lg my-auto w-1/2 lg:w-8/10 text-center">
                    {user}
                </div>
                <div className="my-auto w-1/2 flex justify-evenly md:justify-end">
                    <IconButton
                        disabled={!userName || userName === user}
                        size="small"
                        onClick={isFriend ? removeFriend : addFriend}
                    >
                        <Tooltip
                            title={
                                !isFriend
                                    ? t('user.add_friend')
                                    : t('user.remove_friend')
                            }
                        >
                            {isFriend ? (
                                <PersonRemoveIcon />
                            ) : (
                                <PersonAddIcon />
                            )}
                        </Tooltip>
                    </IconButton>
                    <IconButton
                        disabled={!userName || userName !== user}
                        size="small"
                        onClick={changePicture}
                    >
                        <Tooltip title={'Change Image'}>
                            <ImageIcon />
                        </Tooltip>
                    </IconButton>
                    <div className="my-auto">
                        {imageURL ? (
                            <Link href={imageURL} external>
                                <IconButton size="small">
                                    <Tooltip title="Show Full Image">
                                        <OpenInFullIcon />
                                    </Tooltip>
                                </IconButton>
                            </Link>
                        ) : null}
                    </div>
                </div>
                {error ? (
                    <ErrorNote error={error} onClick={() => setError(null)} />
                ) : (
                    ''
                )}
            </div>
            <div className="relative group h-auto">
                <div
                    className={cn(
                        'hover:grayscale-0 flex flex-wrap rounded-b-md',
                        'w-full h-full ',
                    )}
                    style={{ background: bgColor }}
                >
                    <img
                        onClick={() => toggleImage(imageURL)}
                        src={imageURL}
                        alt="none"
                        className={IMG_CLASS}
                    />
                </div>
            </div>
        </div>
    )
}
